import React from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { useParameterizedQuery, useMutation } from 'react-fetching-library';
import { Box, Modal } from '@mui/material';
import { styled } from '@mui/material/styles';

import { plPL } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { DataGridToolbar } from '../../../../components/data-grid/DataGridToolbar';
import { documentColumns } from '../../../../services/column-definitions';
import { invoiceTypes } from '../../../../services/constants';
import { getInvoiceDocuments, changeDGCSStatusAction, submitInvoiceAction, changeFiscalizationStatusAction } from '../../../../services/request';
import { DocumentBase } from '../../order/order-details/tabs/SettlementTab/documents/DocumentBase';
import SendDocumentModal from './SendDocumentModal';
import PDFPreviewModal from '../../order/order-details/tabs/DocumentAndNotificationsTab/PDFPreviewModal';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { AlertDialogContainer } from '../../../../components/others/AlertDialogContainer';
import toast from 'react-hot-toast';

const RootStyle = styled('div')({
  flexGrow: 1,
  display: 'flex',
  overflow: 'hidden',
  flexDirection: 'column'
});

export const DocumentList = props => {
  const { onOpenSidebar, hideSidebar, documents, queryCallback, loading, setDocuments, editSignal, downloadDocumentCallback, updateDocumentListCallback, deleteCallback } = props;
  const { query } = useParameterizedQuery(getInvoiceDocuments);
  const { mutate } = useMutation(changeDGCSStatusAction);
  const { mutate: invoiceMutation } = useMutation(submitInvoiceAction);
  const { mutate: fiscalizationMutation } = useMutation(changeFiscalizationStatusAction);
  const { season, module } = useSelector(state => state.app);

  const [open, setOpen] = React.useState(false);
  const [editData, setEditData] = React.useState({});
  const [sendData, setSendData] = React.useState({});
  const [openSendModal, setOpenSendModal] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [confirmStatusChange, setConfirmStatusChange] = React.useState(false);
  const [confirmFiscalizationStatusChange, setConfirmFiscalizationStatusChange] = React.useState(false);
  const [tempData, setTempData] = React.useState({});
  const [idToDelete, setIdToDelete] = React.useState(null);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const handleOpenSendModal = row => {
    setOpenSendModal(true);
    setSendData(row);
  };

  const handleCloseSendModal = () => {
    setOpenSendModal(false);
    setSendData({});
  };

  const handleDetailsCheck = data => {
    editSignal(data, data.order ? false : true);
  };

  const handleDownloadDocument = row => {
    downloadDocumentCallback(row.id, row.number);
  };

  const handleShowDocument = async row => {
    const { payload, error, loading } = await query(row.id);
    if (!error && !loading) {
      setPreviewUrl(window.URL.createObjectURL(payload));
    } else {
      toast.error('Nie udało się zoabczyć dokumentu');
    }
  };

  const openConfirmationWindow = (row, bool) => {
    setTempData({ row, 'markAsReady': bool });
    setConfirmStatusChange(true);
  };
  const handleChangeDGCSStatus = async () => {
    const { error, payload } = await mutate(tempData.row.id, tempData);
    if (!error) {
      updateDocumentListCallback(payload);
      toast.success(tempData.markAsReady ? 'Dokument jest teraz na liście oczekujących do pobrania przez system DGCS' : 'Usunięto dokument z listy oczekujących na transfer DGCS');
    } else {
      toast.error('Nie udało się zaktualizować statusu dokumentu');
    }
  };

  const openFiscalizationWindow = (row, bool, error = false) => {
    setTempData({ row, 'markAsReady': bool, error });
    setConfirmFiscalizationStatusChange(true);
  };
  const handleChangeFiscalizationStatus = async () => {
    const { error, payload } = await fiscalizationMutation(tempData.row.id, tempData);
    if (!error) {
      updateDocumentListCallback(payload);
      toast.success(tempData.markAsReady ? 'Dokument jest teraz na liście oczekujących do fiskalizacji' : 'Usunięto dokument z listy oczekujących na fiskalizację');
    } else {
      toast.error('Nie udało się zaktualizować statusu dokumentu');
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await invoiceMutation([], 'DELETE', idToDelete);
    if (!error) {
      deleteCallback(idToDelete);
      setIdToDelete(null);
      setSeverity('success');
      setTitle('Usunięto dokument');
      setMessage('');
      setShowAlert(true);
    } else {
      setSeverity('error');
      setTitle('Nie udało się usunąć dokumentu');
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleConfirmDelete = id => {
    setConfirmDeleteOpen(true);
    setIdToDelete(id);
  };

  const [columns, setColumns] = React.useState(documentColumns(handleDetailsCheck, handleDownloadDocument, handleOpenSendModal, handleShowDocument, openConfirmationWindow, handleConfirmDelete, openFiscalizationWindow));

  const handleOpen = row => {
    setEditData(row);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const submitCallback = (method, invoice) => {
    if (method === 'edit') {
      setDocuments(prev => prev.map(item => {
        if (item.id === invoice.id) {
          return invoice;
        }

        return item;
      }));

      toast.success('Dokument został edytowany');
    }
  };


  const searchCallback = search => {
    queryCallback(search);
  };

  return (
    <RootStyle sx={{ opacity: loading ? 0.5 : 1 }}>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <Box sx={{ minWidth: { md: 800 }, height: '100%' }}>
        <DataGridPro
          columns={columns}
          rows={documents}
          density="compact"
          disableColumnMenu={true}
          autoPageSize={false}
          // autoHeight={true}
          rowsPerPageOptions={[10, 25, 50, 100]}
          pagination
          components={{
            Toolbar: DataGridToolbar
          }}
          initialState={{ pinnedColumns: { left: ['Edytuj', 'Pobierz'] } }}
          componentsProps={{ toolbar: { onOpenSidebar, hideSidebar, miniView: false, searchCallback } }}
          localeText={plPL.components.MuiDataGrid.defaultProps.localeText}
          disableSelectionOnClick
        />
      </Box>
      <Modal open={open} onClose={handleClose}>
        <Box>
          <DocumentBase
            order={{ id: editData.order }}
            editData={editData}
            type={editData.type_and_code}
            season={season}
            module={module}
            closeDialog={handleClose}
            submitCallback={submitCallback}
          />
        </Box>
      </Modal>
      <SendDocumentModal
        open={openSendModal}
        handleClose={handleCloseSendModal}
        data={sendData}
      />
      {previewUrl !== null ? <PDFPreviewModal url={previewUrl} handleClose={() => setPreviewUrl(null)}/> : null}
      <ConfirmDialog
        title={tempData.markAsReady ? 'Oznaczyć jako gotowe do wysyłki do systemu DGCS?' : 'Odznaczyć jako gotowe do wysyłki do systemu DGCS?'}
        open={confirmStatusChange}
        setOpen={setConfirmStatusChange}
        onConfirm={handleChangeDGCSStatus}
      >
        {tempData.markAsReady ? 'Czy na pewno chcesz oznaczyć ten dokument jako gotowy do wysyłki do systemu DGCS?' : 'Dokument czeka na pobranie przez system DGCS. Czy na pewno chcesz go odznaczyć?'}
      </ConfirmDialog>
      <ConfirmDialog
        title="Usunąć Dokument?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
      >
        Czy na pewno chcesz usunąć ten dokument?
      </ConfirmDialog>

      <ConfirmDialog
        title={tempData.error ? 'Wystąpił błąd. Czy chcesz ponownie oznaczyć jako gotowe do fiskalizacji?' : tempData.markAsReady ? 'Oznaczyć jako gotowe do fiskalizacji?' : 'Odznaczyć jako gotowe do fiskalizacji?'}
        open={confirmFiscalizationStatusChange}
        setOpen={setConfirmFiscalizationStatusChange}
        onConfirm={handleChangeFiscalizationStatus}
      >
        {tempData.error ? 'Spowoduje to zrestartowanie procesu fiskalizacji i rozpoczęcie od nowa' : tempData.markAsReady ? 'Czy na pewno chcesz oznaczyć ten dokument jako gotowy do fiskalizacji?' : 'Dokument czeka na fiskalizację. Czy na pewno chcesz go odznaczyć?'}
      </ConfirmDialog>
    </RootStyle>
  );
};
